// ChildDetails.jsx
import React from 'react';

const ChildDetails = ({ childData, mode }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('uk-UA');
    } catch (error) {
      return dateString;
    }
  };

  const getGenderName = (gender) => {
    return gender === 'male' ? 'Хлопчик' : 'Дівчинка';
  };

  const getPlaceTypeName = (placeType) => {
    const typeMap = {
      'standart': 'Повного дня',
      'without-bed': 'Короткотривале',
      'inclusive': 'Інклюзивне',
      'inclusive-short': 'Інклюзивне короткотривале'
    };
    return typeMap[placeType] || placeType;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'active': { class: 'status-verified', label: 'Активний' },
      'graduated': { class: 'status-success', label: 'Випуск' },
      'transferred': { class: 'status-warning', label: 'Переведений' },
      'left': { class: 'status-error', label: 'Вибуття' },
      'inactive': { class: 'status-pending', label: 'Неактивний' }
    };
    
    const config = statusConfig[status] || statusConfig.inactive;
    return <span className={`status-badge ${config.class}`}>{config.label}</span>;
  };

  return (
    <div className="child-details">
      <div className="review-section">
        {/* 1. Про ребенка */}
        <div className="review-block">
          <h3 className="review-title">Про дитину</h3>
          <div className="review-content">
            <div className="review-row">
              <span className="review-label">Прізвище:</span>
              <span className="review-value">{childData.lastName}</span>
            </div>
            <div className="review-row">
              <span className="review-label">Ім'я:</span>
              <span className="review-value">{childData.firstName}</span>
            </div>
            <div className="review-row">
              <span className="review-label">По батькові:</span>
              <span className="review-value">{childData.middleName}</span>
            </div>
            <div className="review-row">
              <span className="review-label">Дата народження:</span>
              <span className="review-value">{formatDate(childData.birthDate)}</span>
            </div>
            <div className="review-row">
              <span className="review-label">Стать:</span>
              <span className="review-value">{getGenderName(childData.gender)}</span>
            </div>
          </div>
        </div>

        {/* 2. Свидетельство о рождении */}
        <div className="review-block">
          <h3 className="review-title">Свідоцтво про народження</h3>
          <div className="review-content">
            <div className="review-row">
              <span className="review-label">Номер:</span>
              <span className="review-value">{childData.birthCertificate?.number}</span>
            </div>
            <div className="review-row">
              <span className="review-label">Дата видачі:</span>
              <span className="review-value">
                {formatDate(childData.birthCertificate?.issueDate)}
              </span>
            </div>
            <div className="review-row">
              <span className="review-label">Ким видано:</span>
              <span className="review-value">{childData.birthCertificate?.issuedBy}</span>
            </div>
            
            {/* Скан копия */}
            {childData.birthCertificateScan && (
              <div className="review-row">
                <span className="review-label">Скан-копія:</span>
                <span className="review-value">
                  <button 
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => childData.onDownloadDocument?.(childData.birthCertificateScan)}
                  >
                    📄 Скачати
                  </button>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* 3. Льготы */}
        <div className="review-block">
          <h3 className="review-title">Пільги</h3>
          <div className="review-content">
            {childData.benefits?.map((benefit, index) => (
              <div key={benefit.id || index} className="benefit-section">
                <div className="review-row">
                  <span className="review-label">Пільга {index + 1}:</span>
                  <span className="review-value benefit-name">{benefit.benefitName}</span>
                </div>
                
                <div className="review-row">
                  <span className="review-label">Тип документу:</span>
                  <span className="review-value">{benefit.documentType}</span>
                </div>
                
                <div className="review-row">
                  <span className="review-label">Номер документу:</span>
                  <span className="review-value">{benefit.documentNumber}</span>
                </div>
                
                {benefit.documentIssueDate && (
                  <div className="review-row">
                    <span className="review-label">Дата видачі:</span>
                    <span className="review-value">
                      {formatDate(benefit.documentIssueDate)}
                    </span>
                  </div>
                )}
                
                {benefit.documentTo && (
                  <div className="review-row">
                    <span className="review-label">На кого видано:</span>
                    <span className="review-value">{benefit.documentTo}</span>
                  </div>
                )}
                
                {benefit.approved && (
                  <div className="review-row">
                    <span className="review-label">Статус підтвердження:</span>
                    <span className="review-value">
                      <span className="status-badge status-verified">
                        Підтверджено
                        {benefit.approvedBy && ` (${benefit.approvedBy})`}
                      </span>
                    </span>
                  </div>
                )}
              </div>
            ))}
            
            {(!childData.benefits || childData.benefits.length === 0) && (
              <div className="review-row">
                <span className="review-label">Пільги:</span>
                <span className="review-value">Відсутні</span>
              </div>
            )}
          </div>
        </div>

        {/* 4. Место регистрации и проживания */}
        <div className="review-block">
          <h3 className="review-title">Місце реєстрації та проживання</h3>
          <div className="review-content">
            <div className="review-row">
              <span className="review-label">Адреса фактичного проживання:</span>
              <span className="review-value">
                { childData.actualAddress || ''}
              </span>
            </div>
            <div className="review-row">
              <span className="review-label">Адреса місця реєстрації:</span>
              <span className="review-value">
                {childData.registeredAddress || ''}
              </span>
            </div>
            <div className="review-row">
              <span className="review-label">Статус РТГК:</span>
              <span className="review-value">
                <span className={`status-badge ${
                  childData.registrationStatus === 'verified' 
                    ? 'status-verified' 
                    : 'status-pending'
                }`}>
                  {childData.registrationStatus === 'verified' 
                    ? 'Підтверджено' 
                    : 'Не підтверджено'}
                </span>
                <span className="status-text ms-2">
                  {childData.registrationStatus === 'verified' 
                    ? 'Реєстрація підтверджена в РТГК' 
                    : 'Реєстрація не підтверджена в РТГК'}
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* 5. Текущий статус */}
        <div className="review-block">
          <h3 className="review-title">Поточний статус</h3>
          <div className="review-content">
            <div className="review-row">
              <span className="review-label">Поточний дитячий заклад:</span>
              <span className="review-value">
                {childData.currentKindergarten || 'Не визначено'}
              </span>
            </div>
            
            {childData.previousKindergarten && (
              <div className="review-row">
                <span className="review-label">Попередній дитячий заклад:</span>
                <span className="review-value">{childData.previousKindergarten}</span>
              </div>
            )}
            
            <div className="review-row">
              <span className="review-label">Поточна група:</span>
              <span className="review-value">
                {childData.currentGroup || 'Не визначено'}
              </span>
            </div>
            
            <div className="review-row">
              <span className="review-label">Тип місця в групі:</span>
              <span className="review-value">
                {getPlaceTypeName(childData.placeType)}
              </span>
            </div>
            
            <div className="review-row">
              <span className="review-label">Статус перебування:</span>
              <span className="review-value">
                {getStatusBadge(childData.status)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildDetails;